import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { fetchTrucks, fetchMetrics } from "../../utils/matchingApi";
import { bodyMatches } from "./catalog";

export const SEARCH_FORBIDDEN_TEXT = "Поиск транспорта недоступен для вашей роли";

/**
 * Live truck supply from the matching service (GET /m/trucks), as offered for «Отправить заявку»:
 * the viewer's own trucks and BUSY («В рейсе») trucks are excluded.
 * `forbidden` is true when the role has no access (HTTP 403) — pages show a message instead of «0 машин».
 */
export function useLiveTrucks() {
  const userId = useSelector((s) => s.auth?.user?.id);
  const [state, setState] = useState({ raw: [], loading: true, error: null, forbidden: false });
  const load = useCallback(() => {
    setState((s) => ({ ...s, loading: true, error: null, forbidden: false }));
    return fetchTrucks()
      .then((d) => setState({ raw: d?.trucks || [], loading: false, error: null, forbidden: false }))
      .catch((e) =>
        setState({
          raw: [],
          loading: false,
          forbidden: e.status === 403,
          error: e.status === 403 ? SEARCH_FORBIDDEN_TEXT : e.message || "Ошибка загрузки",
        }),
      );
  }, []);
  useEffect(() => {
    load();
  }, [load]);
  const trucks = useMemo(
    () => state.raw.filter((t) => t.status !== "BUSY" && !(userId && t.driver_id === userId)),
    [state.raw, userId],
  );
  return { trucks, loading: state.loading, error: state.error, forbidden: state.forbidden, reload: load };
}

/** MVP metrics (dispatcher/admin only on the backend) — null when not allowed. */
export function useMetrics(role) {
  const [m, setM] = useState(null);
  const allowed = role === "DISPATCHER" || role === "ADMIN";
  useEffect(() => {
    if (!allowed) return undefined; // the endpoint answers 403 for other roles — don't even ask
    let alive = true;
    fetchMetrics()
      .then((d) => alive && setM(d))
      .catch(() => alive && setM(null));
    return () => {
      alive = false;
    };
  }, [allowed]);
  return allowed ? m : null;
}

export const EMPTY_FILTERS = {
  origin: "",
  destination: "",
  radius: "all",
  body: "Любой",
  category: "Любая",
  capacity: "0",
  available: "any", // legacy today/tomorrow (old saved searches); the form uses dateFrom/dateTo
  dateFrom: "", // «Когда свободен» / «Дата погрузки» range, YYYY-MM-DD
  dateTo: "",
  volume: "",
  cargo: "all", // «Тип груза» (loads only, ORDER_TYPES id)
  activeOnly: false,
  carrier: "",
  plate: "",
  rate: "", // legacy «ставка до» (kept for old saved searches; not shown)
  price: "", // «Ставка от» — shown on every search window
  verified: false, // «Только проверенные» — applied by the search pages (company profile `verified`)
};

const norm = (s) => String(s ?? "").trim().toLowerCase();
/** "Бишкек, Кыргызстан" -> "бишкек" */
const cityKey = (s) => norm(String(s ?? "").split(",")[0]);

export function haversine(a, b) {
  const R = 6371;
  const rad = (x) => (x * Math.PI) / 180;
  const dLat = rad(b.lat - a.lat);
  const dLng = rad(b.lng - a.lng);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

const dateStr = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const todayStr = () => dateStr(new Date());
const tomorrowStr = () => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return dateStr(d);
};
const freeDay = (t) => (t.available_from ? String(t.available_from).slice(0, 10) : null);

export const destCities = (t) => (t.destinations || []).map((d) => d.city).filter(Boolean);

/** Client-side filtering of live trucks. Adds `_dist` (km from the «Откуда» centre) when known. */
export function filterTrucks(trucks, f) {
  const o = cityKey(f.origin);
  const d = cityKey(f.destination);
  const radius = f.radius === "all" ? null : Number(f.radius);
  let center = null;
  if (o && radius) {
    const pts = trucks.filter((t) => cityKey(t.city) === o && t.lat != null && t.lng != null);
    if (pts.length) {
      center = {
        lat: pts.reduce((s, t) => s + t.lat, 0) / pts.length,
        lng: pts.reduce((s, t) => s + t.lng, 0) / pts.length,
      };
    }
  }
  const minW = Number(f.capacity) || 0;
  const minV = Number(f.volume) || 0;
  const carrier = norm(f.carrier);
  const plate = norm(f.plate);
  const maxRate = Number(f.rate) || 0;
  const minPrice = Number(f.price) || 0;
  const today = todayStr();
  const tomorrow = tomorrowStr();
  const out = [];
  for (const t of trucks) {
    let dist = null;
    if (center && t.lat != null && t.lng != null) dist = Math.round(haversine(center, t));
    if (o) {
      const same = cityKey(t.city).includes(o);
      const near = center && dist != null && dist <= radius;
      if (!same && !near) continue;
    }
    if (d) {
      const ds = destCities(t);
      // a truck without stated destinations is open to any direction
      if (ds.length && !ds.some((c) => cityKey(c).includes(d))) continue;
    }
    if (!bodyMatches(f.body, t.body_type_name)) continue;
    if (f.category && f.category !== "Любая" && t.category_name !== f.category) continue;
    if (minW && (t.max_load_weight ?? 0) < minW) continue;
    if (minV && (t.max_load_volume ?? 0) < minV) continue;
    if (f.activeOnly && t.status !== "ACTIVE") continue;
    if (carrier && !norm(t.driver_name).includes(carrier)) continue;
    if (plate && !norm(t.government_number).includes(plate)) continue;
    // rates are only comparable in the form's currency (сом); trucks with no stated rate drop out
    const isSom = !t.currency || t.currency === "KGS";
    if (maxRate && !(t.min_price != null && isSom && t.min_price <= maxRate)) continue;
    if (minPrice && !(t.min_price != null && isSom && t.min_price >= minPrice)) continue;
    const fd = freeDay(t);
    if (f.available === "today" && fd && fd > today) continue;
    if (f.available === "tomorrow" && fd && fd > tomorrow) continue;
    // free at some point of the range: available from before its end, and not only until before its start
    if (f.dateTo && fd && fd > f.dateTo) continue;
    const until = t.available_until ? String(t.available_until).slice(0, 10) : null;
    if (f.dateFrom && until && until < f.dateFrom) continue;
    out.push({ ...t, _dist: dist });
  }
  return out;
}

export function sortTrucks(rows, sort) {
  const r = [...rows];
  if (sort === "distance") {
    r.sort((a, b) => (a._dist ?? Infinity) - (b._dist ?? Infinity));
  } else if (sort === "rate") {
    r.sort((a, b) => (a.min_price ?? Infinity) - (b.min_price ?? Infinity));
  } else if (sort === "capacity") {
    r.sort((a, b) => (b.max_load_weight ?? 0) - (a.max_load_weight ?? 0));
  }
  return r; // "relevance": server order (free + freshest first)
}

export function fmtFree(t) {
  const fd = freeDay(t);
  if (!fd || fd <= todayStr()) return "Сегодня";
  if (fd === tomorrowStr()) return "Завтра";
  const [, m, d] = fd.split("-");
  return `${d}.${m}`;
}

/** «08:30» when `available_from` carries a time of day, else "". */
export function fmtFreeTime(t) {
  const raw = t.available_from ? String(t.available_from) : "";
  if (raw.length <= 10) return "";
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return "";
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return hh === "00" && mm === "00" ? "" : `${hh}:${mm}`;
}

export function fmtAgo(min) {
  if (min == null) return "";
  if (min < 1) return "подтв. только что";
  if (min < 60) return `подтв. ${min} мин назад`;
  const h = Math.floor(min / 60);
  if (h < 24) return `подтв. ${h} ч назад`;
  return `подтв. ${Math.floor(h / 24)} дн назад`;
}

export function fmtDuration(sec) {
  if (sec == null) return null;
  if (sec < 60) return `${Math.round(sec)} сек`;
  const m = Math.round(sec / 60);
  if (m < 60) return `${m} мин`;
  const h = Math.floor(m / 60);
  const rest = m % 60;
  return rest ? `${h} ч ${rest} мин` : `${h} ч`;
}

export const nf = (n) => Number(n).toLocaleString("ru-RU");

export function plural(n, one, few, many) {
  const a = Math.abs(n) % 100;
  const b = a % 10;
  if (a > 10 && a < 20) return many;
  if (b > 1 && b < 5) return few;
  if (b === 1) return one;
  return many;
}

export const countWord = (n, forms = ["подходящая машина", "подходящие машины", "подходящих машин"]) =>
  plural(n, ...forms);

/** Distinct values for selects, derived from real data. */
export const knownCities = (trucks) =>
  [...new Set(trucks.flatMap((t) => [t.city, ...destCities(t)]).filter(Boolean))].sort();

/** Trucks per current city, biggest first. */
export function cityCounts(trucks) {
  const m = new Map();
  for (const t of trucks) if (t.city) m.set(t.city, (m.get(t.city) || 0) + 1);
  return [...m.entries()].map(([city, n]) => ({ city, n })).sort((a, b) => b.n - a.n || a.city.localeCompare(b.city));
}

/** Most common city -> destination pairs among live trucks. */
export function popularRoutes(trucks, limit = 3) {
  const m = new Map();
  for (const t of trucks) {
    if (!t.city) continue;
    for (const to of destCities(t)) {
      const k = `${t.city}→${to}`;
      m.set(k, { origin: t.city, destination: to, n: (m.get(k)?.n || 0) + 1 });
    }
  }
  return [...m.values()].sort((a, b) => b.n - a.n).slice(0, limit);
}

/** Nearest truck city for given coordinates. */
export function nearestCity(trucks, lat, lng) {
  let best = null;
  for (const t of trucks) {
    if (t.lat == null || t.lng == null || !t.city) continue;
    const dist = haversine({ lat, lng }, t);
    if (!best || dist < best.dist) best = { city: t.city, dist };
  }
  return best;
}

// ---- localStorage helpers (searches) ----
const RECENT_KEY = "itruck.cab.recentSearches";
const read = (k) => {
  try {
    const v = JSON.parse(window.localStorage.getItem(k) || "[]");
    return Array.isArray(v) ? v : [];
  } catch {
    return [];
  }
};
const write = (k, v) => {
  try {
    window.localStorage.setItem(k, JSON.stringify(v));
  } catch {
    /* storage unavailable */
  }
};

export const loadRecent = () => read(RECENT_KEY);
export function pushRecent(f) {
  const item = { origin: f.origin, destination: f.destination, body: f.body, capacity: f.capacity, ts: Date.now() };
  if (!item.origin && !item.destination) return loadRecent();
  const key = (x) => [x.origin, x.destination, x.body, x.capacity].join("|");
  const list = [item, ...read(RECENT_KEY).filter((x) => key(x) !== key(item))].slice(0, 3);
  write(RECENT_KEY, list);
  return list;
}

export const describeFilters = (f) =>
  [f.body && f.body !== "Любой" ? f.body : null, Number(f.capacity) ? `от ${f.capacity} т` : null]
    .filter(Boolean)
    .join(", ") || "Любые параметры";

/** Text match used by the ⌘K global search over the live trucks. */
export const truckText = (t) =>
  [t.driver_name, t.mark, t.government_number, t.body_type_name, t.city, ...destCities(t), t.status === "BUSY" ? "в рейсе" : "свободен"]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
